import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { acceptTripOffer, declineTripOffer, getTrip } from "@/features/trips";
import {
  normalizeDeclineReason,
  offerRefusalKind,
  offerResponseOutcome,
  type OfferRefusalKind,
  type OfferResponseOutcome,
} from "@/features/trips/lib/offerPolicy";
import { normalizeError } from "@/shared/api/errors";
import { env } from "@/shared/constants/env";
import { AGENT_TRIPS_QUERY_KEY } from "./useAgentOffers";

/**
 * Répondre à une offre — contrat du 2026-08-28, §5.
 *
 * Concentre les quatre subtilités de ces deux routes, pour qu'aucun écran n'ait
 * à les redécouvrir.
 */

export type OfferResponseResult = {
  outcome: OfferResponseOutcome;
  /** Message du serveur, quand il en fournit un d'affichable. */
  message?: string;
  /**
   * Pourquoi le refus, quand il y en a un — §2 du contrat du 2026-09-02.
   *
   * `outcome: "fermer"` ne suffit plus : depuis que l'offre part à trois Mains,
   * un `409` recouvre deux situations opposées. « Une autre a été plus rapide »
   * ne demande rien à la Main ; « vous n'êtes plus disponible » lui demande de
   * terminer la course qu'elle porte.
   */
  refusal?: OfferRefusalKind;
};

export function useOfferResponse(tripId: string) {
  const qc = useQueryClient();
  const [isPending, setIsPending] = useState(false);

  /**
   * Garde SYNCHRONE contre la double tape.
   *
   * `isPending` est un état React : entre deux appuis rapides, il n'a pas
   * encore été rendu. Or `accept` n'accepte AUCUNE clé d'idempotence — un
   * second envoi est indiscernable d'un doublon côté serveur.
   */
  const inFlightRef = useRef(false);
  /** A-t-on déjà tenté ? Sert à interpréter un `409` de renvoi. */
  const attemptedRef = useRef(false);

  const reloadFeed = useCallback(() => {
    void qc.invalidateQueries({ queryKey: AGENT_TRIPS_QUERY_KEY });
  }, [qc]);

  const accept = useCallback(async (): Promise<OfferResponseResult> => {
    if (inFlightRef.current) return { outcome: "reessayer" };
    inFlightRef.current = true;
    const isRetry = attemptedRef.current;
    attemptedRef.current = true;
    setIsPending(true);

    try {
      await acceptTripOffer(tripId);

      // ⚠️ `accept` rend la course AVEC ses arrêts mais SANS les commandes ni
      // les articles, là où `GET /trips/{id}` les inclut. Un écran qui se
      // contenterait de la réponse trouverait `stops[].order` à `null`.
      // On recharge donc la version complète avant d'ouvrir la course.
      const complet = await getTrip(tripId).catch(() => null);
      if (complet) qc.setQueryData(["trips", "detail", tripId], complet);

      reloadFeed();
      return { outcome: "ouvrir" };
    } catch (e) {
      const { status, message } = normalizeError(e);

      // Un `409` sur un RENVOI est un succès probable : la coupure réseau a
      // masqué une première acceptation qui avait abouti. Montrer une erreur
      // mentirait — on recharge, et la liste tranchera.
      if (status === 409 && isRetry) {
        if (env.isDev) console.warn("[offre] 409 sur renvoi — succès probable");
        reloadFeed();
        return { outcome: "ouvrir" };
      }

      const outcome = offerResponseOutcome(status);
      // `fermer` n'est pas une panne : l'offre a changé de main ou expiré.
      // ⚠️ Un `403` en fait partie et n'est PAS une erreur d'authentification :
      // déclencher un rafraîchissement de jeton ou une déconnexion serait une
      // faute — c'est un état métier normal.
      if (outcome === "fermer") reloadFeed();
      return { outcome, message, refusal: offerRefusalKind(status, message) };
    } finally {
      inFlightRef.current = false;
      setIsPending(false);
    }
  }, [tripId, qc, reloadFeed]);

  const decline = useCallback(
    async (rawReason: string): Promise<OfferResponseResult> => {
      if (inFlightRef.current) return { outcome: "reessayer" };
      inFlightRef.current = true;
      setIsPending(true);

      try {
        await declineTripOffer(tripId, { reason: normalizeDeclineReason(rawReason) });
        reloadFeed();
        // §4 du contrat du 2026-09-02 : un refus ne ferme QUE notre offre. Les
        // deux autres Mains de la vague continuent de réfléchir, et la course
        // peut être prise dans la seconde par l'une d'elles.
        //
        // Ne rien supposer, donc : ni que la recherche s'arrête, ni qu'elle
        // repart. L'écran ferme l'offre et se tait.
        return { outcome: "fermer" };
      } catch (e) {
        const { status, message } = normalizeError(e);
        const outcome = offerResponseOutcome(status);
        if (outcome === "fermer") reloadFeed();
        return { outcome, message };
      } finally {
        inFlightRef.current = false;
        setIsPending(false);
      }
    },
    [tripId, reloadFeed],
  );

  return { accept, decline, isPending };
}
